import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useAuthStore from "../../../services/AuthStore";
import { Delete, Edit } from "@mui/icons-material";

export default function News() {
  const { getNews, createNews, updateNews, deleteNewsApi } = useAuthStore();
  const [news, setNews] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentNews, setCurrentNews] = useState({
    id: null,
    title_ru: "",
    title_kz: "",
    description_ru: "",
    description_kz: "",
    images: [],
    public_date: new Date().toISOString(),
  });
  const [isEditing, setIsEditing] = useState(false);

  const handleSaveNews = async () => {
    try {
      if (isEditing) {
        // Вызываем API для обновления (предполагаемая функция из AuthStore)
        await updateNews(currentNews);
      } else {
        // Вызываем API для создания (предполагаемая функция из AuthStore)
        await createNews(currentNews);
      }

      // После успешного сохранения, ЗАНОВО получаем актуальный список с сервера
      getNews()
        .then(newNews => setNews(newNews))
        .catch(error =>
          console.error("Ошибка при обновлении списка новостей:", error),
        );

      handleCloseDialog(); // Закрываем модалку
    } catch (error) {
      console.error("Ошибка при сохранении новости:", error);
      // Тут можно показать пользователю ошибку (Snackbar и т.д.)
    }
  };

  // ОБНОВЛЕННАЯ ФУНКЦИЯ УДАЛЕНИЯ (теперь вызывает API)
  const handleDeleteNews = async id => {
    // Добавим подтверждение
    if (window.confirm("Вы уверены, что хотите удалить эту новость?")) {
      try {
        // Вызываем API для удаления (предполагаемая функция из AuthStore)
        await deleteNewsApi({ id: id }); // Твой Flask ждет JSON с ID

        // Обновляем список, заново получив его с сервера
        getNews()
          .then(newNews => setNews(newNews))
          .catch(error =>
            console.error("Ошибка при обновлении списка новостей:", error),
          );
      } catch (error) {
        console.error("Ошибка при удалении:", error);
      }
    }
  };

  useEffect(() => {
    getNews()
      .then(newNews => setNews(newNews))
      .catch(error => console.error("Ошибка при получении новостей:", error));
  }, [getNews]);

  const handleOpenDialog = (
    newsItem = {
      id: null,
      title_ru: "",
      title_kz: "",
      description_ru: "",
      description_kz: "",
      images: [],
      public_date: new Date().toISOString(),
    },
  ) => {
    setCurrentNews(newsItem);
    setIsEditing(!!newsItem.id);
    setDialogOpen(true);
  };

  const handleRemoveImage = indexToRemove => {
    setCurrentNews(prev => ({
      ...prev,
      // Фильтруем массив, удаляя картинку по ее индексу
      images: prev.images.filter((_, index) => index !== indexToRemove),
    }));
  };

  // Функция для чтения файлов и конвертации в Base64 (добавляем в компонент News)
  const handleImageUpload = e => {
    const files = Array.from(e.target.files);

    // Мы используем Promise.all, чтобы дождаться загрузки ВСЕХ файлов
    const imagePromises = files.map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file); // Читаем файл как Data URL (Base64 с префиксом)

        reader.onload = () => {
          // reader.result содержит "data:image/png;base64,iVBORw0KGgo..."
          // Твоему Flask нужен только "iVBORw0KGgo...", поэтому отрезаем префикс:
          const base64String = reader.result.split(",")[1];
          resolve(base64String);
        };
        reader.onerror = error => reject(error);
      });
    });

    // Когда все файлы сконвертированы:
    Promise.all(imagePromises)
      .then(newImagesBase64 => {
        setCurrentNews(prev => ({
          ...prev,
          // Добавляем новые Base64-строки к тем, что уже были в state
          images: [...prev.images, ...newImagesBase64],
        }));
      })
      .catch(error => console.error("Ошибка при чтении файлов:", error));

    // Сбрасываем значение input, чтобы можно было загрузить тот же файл еще раз
    e.target.value = null;
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentNews({
      id: null,
      title_ru: "",
      title_kz: "",
      description_ru: "",
      description_kz: "",
      images: [],
      public_date: new Date().toISOString(),
    });
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "title_ru", headerName: "Заголовок (RU)", width: 200 },
    { field: "title_kz", headerName: "Заголовок (KZ)", width: 200 },
    {
      field: "public_date",
      headerName: "Дата публикации",
      width: 150,
      renderCell: params => new Date(params.row.public_date).toLocaleString(),
    },
    {
      field: "actions",
      headerName: "Действия",
      width: 200,
      renderCell: params => (
        <Box>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenDialog(params.row)}>
            <Edit />
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={() => handleDeleteNews(params.row.id)}
            style={{ marginLeft: 8 }}>
            <Delete />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h6" style={{ marginBottom: 16 }}>
        Управление новостями
      </Typography>
      <Button
        variant="contained"
        onClick={() => handleOpenDialog()}
        style={{ marginBottom: 16 }}>
        Добавить новость
      </Button>
      <Box sx={{ height: 400, width: "100%" }}>
        <DataGrid
          rows={news}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
        />
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>
          {isEditing ? "Редактировать новость" : "Добавить новость"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Заголовок (RU)"
            type="text"
            fullWidth
            value={currentNews.title_ru}
            onChange={e =>
              setCurrentNews({ ...currentNews, title_ru: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Заголовок (KZ)"
            type="text"
            fullWidth
            value={currentNews.title_kz}
            onChange={e =>
              setCurrentNews({ ...currentNews, title_kz: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Описание (RU)"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={currentNews.description_ru}
            onChange={e =>
              setCurrentNews({ ...currentNews, description_ru: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Описание (KZ)"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={currentNews.description_kz}
            onChange={e =>
              setCurrentNews({ ...currentNews, description_kz: e.target.value })
            }
          />
          <Button variant="outlined" component="label" sx={{ mt: 2, mb: 1 }}>
            Загрузить изображения
            <input
              type="file"
              hidden
              multiple
              accept="image/png, image/jpeg"
              onChange={handleImageUpload} // Эту функцию мы сейчас создадим
            />
          </Button>

          {/* КОНТЕЙНЕР ДЛЯ ПРЕВЬЮ ЗАГРУЖЕННЫХ КАРТИНОК */}
          <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {currentNews.images.map((imgBase64, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  overflow: "hidden",
                }}>
                <img
                  // Добавляем префикс ОБРАТНО, чтобы браузер мог показать картинку
                  src={`data:image/jpeg;base64,${imgBase64}`}
                  alt={`Preview ${index}`}
                  style={{ height: 100, width: "auto", display: "block" }}
                />
                {/* Кнопка удаления превью */}
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
                  }}
                  onClick={() => handleRemoveImage(index)} // Эту функцию тоже создадим
                >
                  <Delete fontSize="small" color="error" />
                </IconButton>
              </Box>
            ))}
          </Box>
          <TextField
            margin="dense"
            label="Дата публикации"
            type="datetime-local"
            fullWidth
            value={currentNews.public_date}
            onChange={e =>
              setCurrentNews({ ...currentNews, public_date: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSaveNews} variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
